import { useMutation } from '@tanstack/react-query';
import { z } from 'zod';
import { useFriend } from '@/hooks/contacts/use-friends';
import { allChatSessionQueryOptions } from '@/hooks/sessions/use-conversation-sessions';
import { generateResponseScheme, type RespondedWith, type ResponseError } from '@/models/response';
import { fetcher } from '@/utils/fetcher';
import { queryClient } from '@/utils/query-client';
import { avatarUrlSchema, nameSchema, stringifiedDateSchema } from '@/utils/schemas';

export default function useCreateSession(userId?: Nullable<number>) {
  const { friend } = useFriend(userId);

  const mutation = useMutation<RespondedWith<CreateSessionResponseType>, ResponseError, number>({
    mutationKey: ['/CreateChatSession', userId],
    mutationFn: (params) =>
      fetcher(
        '/CreateChatSession',
        { UserID: params },
        { schema: generateResponseScheme(createSessionResponseScheme), validResponseCodes: [0] },
      ),
    onSuccess: () => queryClient.invalidateQueries(allChatSessionQueryOptions),
  });

  return {
    sessionId: friend?.ChatSessionID,
    ...mutation,
  };
}

const createSessionResponseScheme = z.object({
  UserAID: z.number(),
  UserANickName: nameSchema,
  UserAHeadImg: avatarUrlSchema,
  UserBID: z.number(),
  UserBNickName: nameSchema,
  UserBHeadImg: avatarUrlSchema,
  CreateTime: stringifiedDateSchema,
  UpdateTime: z.string(),
  LastMsgTimeStamp: z.number(),
  LastMsg: z.string(),
  Status: z.number(),
  ReadDelete: z.number(),
  AutoDeleteTime: z.number(),
  ID: z.number(),
});

type CreateSessionResponseType = z.infer<typeof createSessionResponseScheme>;
